import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '../../kanban-board/crm-theme';

interface Props {
    title: string;
    count?: string;
    action?: { label: string; icon?: ReactNode; onClick: () => void };

    /// Colunas da grade que o bloco ocupa, para fechar a linha: buraco no fim parece falha.
    columns?: number;
    children: ReactNode;
}

/// Todo bloco usa este; height:100% alinha os blocos da mesma linha da grade.
export const LeadBlock = ({ title, count, action, columns, children }: Props) => {
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: '11px',
                gridColumn: columns ? { lg: `span ${columns}` } : undefined,
                p: 1.75,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '.06em',
                        textTransform: 'uppercase',
                        color: cc.textSecondary,
                    }}
                >
                    {title}
                </Typography>

                {count && (
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: cc.textMuted,
                            bgcolor: cc.countBg,
                            borderRadius: '9px',
                            px: 0.9,
                            py: 0.15,
                        }}
                    >
                        {count}
                    </Typography>
                )}

                {action && (
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={action.onClick}
                        onKeyDown={(e) => e.key === 'Enter' && action.onClick()}
                        sx={{
                            ml: 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: cc.accent,
                            cursor: 'pointer',
                        }}
                    >
                        {action.icon}
                        {action.label}
                    </Box>
                )}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
        </Box>
    );
};

interface LineProps {
    label: string;
    value?: ReactNode;
    highlight?: boolean;
}

/// Uma linha de campo: rótulo à esquerda, valor à direita e divisória fina, igual em todos os blocos.
export const LeadField = ({ label, value, highlight }: LineProps) => {
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1.25,
                py: 0.75,
                borderBottom: `1px solid ${cc.divider}`,
                '&:last-of-type': { borderBottom: 'none' },
                fontSize: 12.5,
            }}
        >
            <Typography sx={{ fontSize: 12.5, color: cc.textSecondary }}>{label}</Typography>
            <Typography
                sx={{
                    fontSize: 12.5,
                    fontWeight: value ? 600 : 400,
                    textAlign: 'right',
                    color: highlight ? cc.eventSuccess : value ? cc.textPrimary : cc.textMuted,
                }}
            >
                {value || '—'}
            </Typography>
        </Box>
    );
};
